import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Col, Form, Row, Tooltip } from 'antd';

import { getStyleVariableValue, routeFileSelector } from '@/utilz';
import { ReactComponent as ProductAddIcon } from '@/assets/icons/product-add.svg';
import IconButton from '@/views/components/Buttons/IconButton';
import SvgConverter from '@/views/components/Common/SvgConverter';
import FormInputNumber from '@/views/components/Inputs/FormInputNumber';
import FormTextArea from '@/views/components/Inputs/FormTextArea';
import HiddenFormInput from '@/views/components/Inputs/HiddenFormInput';
import Paper from '@/views/components/Paper/Paper';
import ProductSelector from '@/views/components/Selectors/AsynchSearch/ProductSelector';
import ProductUnitUomSelector from '@/views/components/Selectors/ProductUnitUomSelector';
import { useFocusableRef } from '@/views/screens/Claims/common/hooks/useFocusableRef';
import SaveCancelButtons from '@/views/screens/Shipment/components/SaveCancelButtons';

import {
  ClaimsDispositionMethodSelect,
  ClaimsMitigatedTypeSelect,
  ClaimsPercentageValueSelect,
  RegularReleaseSelect,
} from '../../../selects';

const PreClaimsItemCard = ({
  editMode = false,
  isLastItem = false,
  shadowVisible = true,
  index = 0,
  itemDetails,
  setEditMode,
  handleCancel,
  handleSubmit,
  handleDelete,
  handleNewProductModal,
  onFieldsChange,
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [form] = Form.useForm();

  useEffect(() => {
    if (itemDetails) {
      form.setFieldsValue(itemDetails);
    }
  }, [itemDetails, form]);

  const cardRef = useFocusableRef('#itemCode-origin', { isEnabled: itemDetails?.isNew && !itemDetails?.isInitial });

  const handleProductCodeChange = (value) => {
    if (!value['1']) {
      form.setFieldsValue({
        claimantValue: value.claimantValue,
        fullRelFlag: value.fullRelFlag,
        itemDesc: value?.productDesc,
        itemUnitMeasure: value.productUnitMeasure,
        itemUnitWeight: value.productWeight,
      });
    }
  };

  return (
    <div ref={cardRef}>
      <Paper className={`border-muted ${!isLastItem ? 'mb-16' : ''} ${shadowVisible ? '' : 'shadow-none'}`}>
        <Form
          onFinish={(data) => handleSubmit(data, index)}
          initialValues={itemDetails}
          form={form}
          onFieldsChange={onFieldsChange}
        >
          <div className="border-bottom-muted pt-8 pb-10 px-16">
            <Row justify="space-between align-items-center">
              <Col sm={6}>
                <Row align="middle" gutter={15}>
                  <Col className="flex-1">
                    <Row align="middle" gutter={15}>
                      <Col className="flex-1">
                        <ProductSelector
                          name="itemCode"
                          label="Item Code"
                          placeholder="Select Item Code"
                          singleLineMode
                          margin="m-0"
                          editMode={editMode}
                          onChange={handleProductCodeChange}
                          colon
                          form={form}
                          rules={[
                            {
                              message: t('translation:default.fields.productDesc.message'),
                              required: true,
                            },
                          ]}
                        />
                      </Col>
                      {editMode && (
                        <Col>
                          <IconButton onClick={() => handleNewProductModal(index)}>
                            <Tooltip
                              color={getStyleVariableValue('--label')}
                              title={t('translation:shipmentAdd.productDetails.createNewProduct')}
                              placement="bottomLeft"
                              overlayClassName="mw-none rate-shop-cost-tool-tip"
                            >
                              <SvgConverter Icon={ProductAddIcon} color="header" />
                            </Tooltip>
                          </IconButton>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col>
                <SaveCancelButtons
                  editMode={editMode}
                  lightMode={false}
                  // shipmentEditStatus={shipmentEditStatus}
                  setEditMode={(val) => setEditMode(index, val)}
                  handleRemove={() => handleDelete(index)}
                  isNew={itemDetails.isNew}
                  idSuffix={`product-details-${index + 1}`}
                  isModified={null}
                  onCancel={() => handleCancel(index, itemDetails.isNew)}
                />
              </Col>
            </Row>
          </div>
          <div className="px-15 pt-10">
            <Row gutter={15}>
              <Col sm={8}>
                <FormTextArea
                  label="Item Description"
                  placeholder="Enter Item Description"
                  editMode={editMode}
                  {...(editMode ? { rows: 3 } : {})}
                  name="itemDesc"
                  margin="mb-15"
                  maxLength={25}
                  rules={[
                    {
                      message: 'Please enter Item Description',
                      required: true,
                    },
                  ]}
                />
                <FormInputNumber
                  label="Quantity"
                  placeholder="Enter Quantity"
                  editMode={editMode}
                  name="itemQuantity"
                  margin="mb-15"
                  precision={2}
                  inputType="number"
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[
                    {
                      message: 'Please enter item Quantity',
                      required: true,
                    },
                  ]}
                />
                <ProductUnitUomSelector
                  label="Unit of Measure"
                  placeholder="Enter Unit of Measure"
                  editMode={editMode}
                  name="itemUnitMeasure"
                  margin="mb-15"
                  rules={[
                    {
                      message: 'Please select Unit of Measure',
                      required: true,
                    },
                  ]}
                  shouldUseNativeSelect
                />
              </Col>
              <Col sm={8}>
                <RegularReleaseSelect
                  label="Full/Release Value"
                  placeholder="Enter Full/Release Value"
                  editMode={editMode}
                  name="fullRelFlag"
                  margin="mb-15"
                  rules={[
                    {
                      message: 'Please select Full/Release Value',
                      required: true,
                    },
                  ]}
                />
                <ClaimsDispositionMethodSelect
                  label="Disposition Method"
                  placeholder="Enter Disposition Method"
                  editMode={editMode}
                  name="dispositionMethod"
                  margin="mb-15"
                  rules={[
                    {
                      message: 'Please select Disposition Method',
                      required: true,
                    },
                  ]}
                />
                <FormInputNumber
                  label="Filed Value"
                  placeholder="Enter Filed Value"
                  editMode={editMode}
                  name="value"
                  margin="mb-18"
                  precision={2}
                  inputType="number"
                  allowNegative
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[
                    {
                      message: 'Please enter Filed Value',
                      required: true,
                    },
                  ]}
                />
              </Col>
              <Col sm={8}>
                <ClaimsPercentageValueSelect
                  label="Percentage/Value"
                  placeholder="Enter Percentage/Value"
                  editMode={editMode}
                  name="smType"
                  margin="mb-15"
                />
                <ClaimsMitigatedTypeSelect
                  label="Mitigated Type"
                  placeholder="Enter Mitigated Type"
                  editMode={editMode}
                  name="smFlag"
                  margin="mb-15"
                />
                <FormInputNumber
                  label="Mitigated Amount"
                  placeholder="Enter Mitigated Amount"
                  editMode={editMode}
                  name="smAmount"
                  margin="mb-15"
                  precision={2}
                  inputType="number"
                  allowNegative
                />
              </Col>
            </Row>
          </div>
          <HiddenFormInput name="itemSeq" />
          <HiddenFormInput name="oldCommentType" />
          <HiddenFormInput name="isNew" />
          <HiddenFormInput name="editMode" />
          <HiddenFormInput name="claimKey" />
          <HiddenFormInput name="dirtyFlag" />
          <HiddenFormInput name="claimantValue" />
          <HiddenFormInput name="itemUnitWeight" />
        </Form>
      </Paper>
    </div>
  );
};

export default PreClaimsItemCard;
